import { useEffect, useState } from 'react'
import type { DayDoc, DayResult } from './lib/types'
import { loadDay, todayUtc, BETA_START } from './lib/day'
import { getResult, dailyStreak } from './lib/storage'
import { AuthModal } from './components/AuthModal'
import { HowToPlay } from './components/HowToPlay'
import { TopBar, openAuth, openHowTo } from './components/TopBar'
import { BottomNav } from './components/BottomNav'
import { BonusCard } from './components/BonusCard'
import { isSignedIn } from './lib/user'
import { Starfield } from './components/Starfield'
import { Play } from './components/Play'
import { Results } from './components/Results'
import { Archive } from './components/Archive'
import { Wrapped } from './components/Wrapped'
import { Account } from './components/Account'
import { Plus } from './components/Plus'
import { Practice } from './components/Practice'
import { Bonus } from './components/Bonus'
import { Extended } from './components/Extended'
import { Stats } from './components/Stats'
import { Privacy, Terms } from './components/Legal'
import { isPlus, onUserChange } from './lib/user'

function useHash() {
  const [h, setH] = useState(location.hash || '#/')
  useEffect(() => { const f = () => setH(location.hash || '#/'); addEventListener('hashchange', f); return () => removeEventListener('hashchange', f) }, [])
  return h
}

export default function App() {
  const hash = useHash()
  const [zoom, setZoom] = useState(1)
  const [authOpen, setAuthOpen] = useState(false)
  const [howToOpen, setHowToOpen] = useState(false)
  useEffect(() => {
    const a = () => setAuthOpen(true), h = () => setHowToOpen(true)
    addEventListener('wayoff:auth', a); addEventListener('wayoff:howto', h)
    return () => { removeEventListener('wayoff:auth', a); removeEventListener('wayoff:howto', h) }
  }, [])
  const m = hash.match(/^#\/play\/(\d{4}-\d{2}-\d{2})$/)
  const date = m ? m[1] : todayUtc()
  const replay = !!m && date !== todayUtc()

  const [, force] = useState(0)
  useEffect(() => onUserChange(() => force((x) => x + 1)), [])

  let view: React.ReactNode
  if (hash === '#/archive') view = <Archive />
  else if (hash === '#/wrapped') view = <Wrapped />
  else if (hash === '#/account') view = <Account />
  else if (hash === '#/plus') view = <Plus />
  else if (hash === '#/practice') view = <Practice onZoom={setZoom} />
  else if (hash === '#/stats') view = <Stats />
  else if (hash === '#/bonus') view = <Bonus onZoom={setZoom} />
  else if (hash === '#/ext') view = <Extended onZoom={setZoom} />
  else if (hash === '#/privacy') view = <Privacy />
  else if (hash === '#/terms') view = <Terms />
  else if (replay && !isPlus()) view = <Plus />
  else view = <Game key={date} date={date} replay={replay} onZoom={setZoom} />
  return <>
    <Starfield zoom={zoom} />
    {view}
    {authOpen && <AuthModal onClose={() => setAuthOpen(false)} />}
    {howToOpen && <HowToPlay onClose={() => setHowToOpen(false)} />}
  </>
}

function Game({ date, replay, onZoom }: { date: string; replay: boolean; onZoom: (z: number) => void }) {
  const [day, setDay] = useState<DayDoc | null>(null)
  const [result, setResult] = useState<DayResult | undefined>(() => getResult(date))
  const [started, setStarted] = useState(false)
  const [past, setPast] = useState(false) // anon front screen dismissed this session
  const [err, setErr] = useState('')
  const [, force] = useState(0)
  useEffect(() => onUserChange(() => force((x) => x + 1)), [])

  useEffect(() => {
    if (date < BETA_START && !import.meta.env.DEV) { setErr('That day is before Way Off existed.'); return }
    loadDay(date).then(setDay).catch((e) => setErr(String(e)))
  }, [date])

  if (err) return <div className="screen landing"><p>{err}</p><a className="btn" href="#/">Play today</a></div>
  if (!day) return <div className="screen landing"><p className="muted">Loading today's numbers…</p></div>
  if (result) return <Results day={day} result={result} />
  if (!started && !replay && !isSignedIn() && !past && !sessionStorage.getItem('wayoff.front')) {
    return (
      <div className="screen landing">
        <div style={{ flex: 1 }} />
        <h1>Way<br /><b>Off</b></h1>
        <p className="pitch">A daily quiz where every answer is a number.<br />It could be tiny. It could be enormous.<br />Guess the size.<br /><b className="tagline">You&rsquo;ll be way off.</b></p>
        <div className="actions" style={{ width: '100%' }}>
          <button className="btn block" onClick={() => { sessionStorage.setItem('wayoff.front', '1'); setPast(true) }}>Play Daily</button>
          <button className="btn ghost block" onClick={openHowTo}>How to Play</button>
        </div>
        <button className="login-btn big" onClick={openAuth}>Sign up / Login</button>
        <p className="tiny">keep your streak 🔥 · pick a name 🏆 · save your stats 📊</p>
        <a className="tiny plus-link" href="#/plus">✦ Get Plus</a>
        <div style={{ flex: 1 }} />
        <div className="tiny"><a href="#/privacy">Privacy</a> · <a href="#/terms">Terms</a></div>
      </div>
    )
  }
  if (!started) {
    const streak = dailyStreak(undefined, isPlus())
    return (
      <div className="screen landing">
        <TopBar nav />
        <div style={{ flex: 1 }} />
        <h1>Way<br /><b>Off</b></h1>
        <p className="pitch">Real numbers, tiny to enormous. Guess the size.<br /><b className="tagline">You&rsquo;ll be way off.</b></p>
        <div className="card" style={{ textAlign: 'left' }}>
          <h3>{replay ? 'Replay' : 'Today'}{day.number > 0 ? ` · #${day.number}` : ''}</h3>
          <div className="stat">{day.theme.emoji} {day.theme.name}</div>
          <div className="tiny">5 questions · one theme{day.local ? ' · offline set' : ''}</div>
        </div>
        <button className="btn block" onClick={() => setStarted(true)}>Play</button>
        <BonusCard />
        {isPlus() && (
          <a className="card upsell" href="#/practice" style={{ width: '100%' }}>
            <span>🎲</span><span><b>Practice</b> — a fresh random 5, doesn't touch your streak</span><span>→</span>
          </a>
        )}
        <div className="meta">{streak > 0 ? `🔥 ${streak}-day streak` : 'no typing — two taps per answer'}</div>
        <div className="tiny"><a href="#/privacy">Privacy</a> · <a href="#/terms">Terms</a></div>
        <div style={{ flex: 1 }} />
        <BottomNav />
      </div>
    )
  }
  return <Play day={day} replay={replay} onZoom={onZoom} onDone={setResult} />
}
